import {
  ProviderAutomation,
  ProviderAutomationFetchParams,
  ProviderAutomationParams,
  ProviderChecklist,
  ProviderChecklistFetchParams,
  ProviderChecklistItem,
  ProviderChecklistItemParams,
  ProviderChecklistItemUploadParams,
  ProviderChecklistParams,
  ProviderTemplate,
  ProviderTemplateFetchParams,
  ProviderTemplateItem,
  ProviderTemplateItemParams,
  ProviderTemplateParams,
  ProviderTemplateSection,
  ProviderTemplateSectionParams,
} from './types'

/**
 * Base provider class - this is the interface that all providers must implement
 * to be compatible with the store.
 */
export default abstract class BaseProvider {
  // Templates
  abstract fetchTemplates(
    params?: ProviderTemplateFetchParams
  ): Promise<ProviderTemplate[]>
  abstract fetchTemplate(id: string): Promise<ProviderTemplate | undefined>
  abstract createTemplate(
    params: ProviderTemplateParams
  ): Promise<ProviderTemplate>
  abstract updateTemplate(
    id: string,
    params: ProviderTemplateParams
  ): Promise<ProviderTemplate>
  abstract deleteTemplate(id: string): Promise<boolean>

  // Template Items
  abstract fetchTemplateItems(
    templateId: string
  ): Promise<ProviderTemplateItem[]>
  abstract createTemplateItem(
    templateId: string,
    params: ProviderTemplateItemParams
  ): Promise<ProviderTemplateItem>
  abstract updateTemplateItem(
    templateId: string,
    itemId: string,
    params: ProviderTemplateItemParams
  ): Promise<ProviderTemplateItem>
  abstract deleteTemplateItem(
    templateId: string,
    itemId: string
  ): Promise<boolean>
  abstract reorderTemplateItems(
    id: string,
    sectionsAndItemsMap: Record<string, string[]>
  ): Promise<ProviderTemplate | undefined>

  abstract fetchTemplateSections(
    templateId: string
  ): Promise<ProviderTemplateSection[]>
  abstract createTemplateSection(
    templateId: string,
    params: ProviderTemplateSectionParams
  ): Promise<ProviderTemplateSection>
  abstract deleteTemplateSection(
    templateId: string,
    id: string
  ): Promise<boolean>

  abstract fetchChecklists(
    params: ProviderChecklistFetchParams
  ): Promise<ProviderChecklist[]>
  abstract fetchChecklist(
    checklistId: string
  ): Promise<ProviderChecklist | undefined>
  abstract createChecklist(name: string): Promise<ProviderChecklist>
  abstract createChecklistFromTemplate(
    templateId: string
  ): Promise<ProviderChecklist>
  abstract createChecklistFromChecklist(
    checklistId: string
  ): Promise<ProviderChecklist>
  abstract updateChecklist(
    checklistId: string,
    params: ProviderChecklistParams
  ): Promise<ProviderChecklist>
  abstract deleteChecklist(checklistId: string): Promise<boolean>

  abstract createChecklistItem(
    checklistId: string,
    params: ProviderChecklistItemParams
  ): Promise<ProviderChecklistItem>
  abstract updateChecklistItem(
    checklistId: string,
    itemId: string,
    params: ProviderChecklistItemParams
  ): Promise<ProviderChecklistItem>
  abstract deleteChecklistItem(
    checklistId: string,
    itemId: string
  ): Promise<ProviderChecklistItem>
  abstract getChecklistItemUploadUrl(
    checklistId: string,
    itemId: string,
    params: ProviderChecklistItemUploadParams
  ): Promise<{ url: string; filename: string }>
  abstract getTemplateItemUploadUrl(
    templateId: string,
    itemId: string,
    params: ProviderChecklistItemUploadParams
  ): Promise<{ url: string; filename: string }>
  abstract getChecklistItemFileUrl(
    checklistId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }>
  abstract getChecklistItemDescriptionFileUrl(
    checklistId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }>
  abstract getTemplateItemDescriptionFileUrl(
    templateId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }>

  // automations
  abstract fetchAutomations(
    params?: ProviderAutomationFetchParams
  ): Promise<ProviderAutomation[]>
  abstract createAutomation(
    params: ProviderAutomationParams
  ): Promise<ProviderAutomation>
  abstract updateAutomation(
    id: string,
    params: ProviderAutomationParams
  ): Promise<ProviderAutomation>
  abstract deleteAutomation(id: string): Promise<boolean>

  abstract getPushSubscription(clientId: string): Promise<{ payload: string }>

  abstract updatePushSubscription(
    clientId: string,
    payload: string
  ): Promise<void>

  abstract deletePushSubscription(clientId: string): Promise<void>
}
